import { useAuth } from "@elit-hair/elit-tools";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  EmptyState,
  ErrorState,
  Icon,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbPlus, TbRefresh, TbX } from "react-icons/tb";
import { toast } from "sonner";

import { PaginationControls } from "@/components/ui/pagination-controls";
import { SearchBar } from "@/components/ui/search-bar";
import { getTotalPages, PAGINATION } from "@/constants/pagination";
import { PERMISSIONS } from "@/constants/permissions";
import {
  useDeletePermissionGroup,
  usePermissionGroups,
} from "@/hooks/queries/permission-group.queries";
import { PermissionGroupCard } from "@/pages/permission-groups/components/permission-group-card";
import { PermissionGroupFormDialog } from "@/pages/permission-groups/components/permission-group-form-dialog";
import { PermissionGroupsTable } from "@/pages/permission-groups/components/permission-groups-table";
import { PERMISSION_GROUPS_PAGE_SIZE } from "@/pages/permission-groups/constants";
import { PermissionGroupsSearchParamsSchema } from "@/pages/permission-groups/types/permission-groups-search.types";
import {
  extractApiMessage,
  filterPermissionGroups,
} from "@/pages/permission-groups/utils/permission-groups.utils";
import { IPermissionGroup } from "@/types/permission-group.types";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";

type FormDialogState = "create" | IPermissionGroup | null;

export const Route = createFileRoute(APP_ROUTES_FULL.PERMISSION_GROUPS)({
  component: PermissionGroupsPage,
  validateSearch: zodValidator(PermissionGroupsSearchParamsSchema),
  staticData: {
    config: {
      breadcrumb: {
        title: "permission_groups",
        to: APP_ROUTES_TO.PERMISSION_GROUPS,
      },
      title: "permission_groups",
      layout: { scroll: false },
    },
    permissions: [PERMISSIONS.AUTH_SVC_PERMISSIONS_GROUPS_LIST],
  },
});

function PermissionGroupsPage() {
  const { t } = useTranslation();
  const navigate = Route.useNavigate();
  const searchParams = Route.useSearch({ structuralSharing: true });
  const { userData } = useAuth();
  const permissionCodes = userData?.permissionCodes || [];

  const canCreate = permissionCodes.includes(
    PERMISSIONS.AUTH_SVC_PERMISSIONS_GROUPS_CREATE,
  );
  const canUpdate = permissionCodes.includes(
    PERMISSIONS.AUTH_SVC_PERMISSIONS_GROUPS_UPDATE,
  );
  const canDelete = permissionCodes.includes(
    PERMISSIONS.AUTH_SVC_PERMISSIONS_GROUPS_DELETE,
  );

  const page = searchParams.page ?? PAGINATION.DEFAULT_PAGE;
  const searchQuery = searchParams.search?.trim() ?? "";

  const [searchInput, setSearchInput] = useState(searchQuery);
  const [formDialog, setFormDialog] = useState<FormDialogState>(null);
  const [deleteTarget, setDeleteTarget] = useState<IPermissionGroup | null>(
    null,
  );

  useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);

  const handleDebouncedSearch = useCallback(
    (value: string) => {
      navigate({
        to: Route.to,
        search: (old) => ({
          ...old,
          search: value || undefined,
          page: undefined,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePageChange = (nextPage: number) => {
    navigate({
      to: Route.to,
      search: (old) => ({
        ...old,
        page: nextPage === PAGINATION.DEFAULT_PAGE ? undefined : nextPage,
      }),
      replace: true,
    });
  };

  const clearFilters = () => {
    setSearchInput("");
    navigate({
      to: Route.to,
      search: (old) => ({
        ...old,
        search: undefined,
        page: undefined,
      }),
      replace: true,
    });
  };

  const { data, isLoading, isFetching, isError, error, refetch } =
    usePermissionGroups();
  const deleteMutation = useDeletePermissionGroup();

  const filteredGroups = useMemo(
    () => filterPermissionGroups(data ?? [], searchQuery),
    [data, searchQuery],
  );

  const total = filteredGroups.length;
  const totalPages = Math.max(
    1,
    getTotalPages(total, PERMISSION_GROUPS_PAGE_SIZE),
  );
  const safePage = Math.min(page, totalPages);
  const paginatedGroups = filteredGroups.slice(
    (safePage - 1) * PERMISSION_GROUPS_PAGE_SIZE,
    safePage * PERMISSION_GROUPS_PAGE_SIZE,
  );

  const hasActiveFilters = searchQuery.length > 0;
  const isRefreshing = isFetching && !!data;

  useEffect(() => {
    if (page > totalPages) {
      handlePageChange(totalPages);
    }
  }, [page, totalPages]);

  const handleFormSuccess = (action: "created" | "updated") => {
    setFormDialog(null);
    toast.success(
      action === "created"
        ? t("permission_group_created")
        : t("permission_group_updated"),
    );
    if (action === "created") {
      handlePageChange(PAGINATION.DEFAULT_PAGE);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast.success(t("permission_group_deleted"));
      setDeleteTarget(null);
    } catch (err) {
      toast.error(
        extractApiMessage(err, t("failed_to_delete_permission_group")),
      );
    }
  };

  if (isError) {
    return (
      <ErrorState
        title={t("failed_to_load_permission_groups")}
        message={extractApiMessage(error, t("try_again_later"))}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-hidden">
      <div className="flex shrink-0 flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {t("permission_groups")}
          </h1>
          <p className="text-muted-foreground">
            {isLoading && !data
              ? t("permission_groups_page_description")
              : t("permission_groups_count", { count: total })}
          </p>
        </div>

        <div className="flex w-full flex-wrap gap-x-2 gap-y-3 sm:w-auto">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                onClick={() => refetch()}
                disabled={isRefreshing}
              >
                {isRefreshing ? (
                  <Spinner size="sm" className="md:mr-2" />
                ) : (
                  <Icon icon={TbRefresh} className="md:mr-2" />
                )}
                <span className="hidden md:inline">{t("refresh")}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("refresh")}</TooltipContent>
          </Tooltip>

          {canCreate && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button onClick={() => setFormDialog("create")}>
                  <Icon icon={TbPlus} className="md:mr-2" />
                  <span className="hidden md:inline">
                    {t("add_permission_group")}
                  </span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("add_permission_group")}</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-3">
        <SearchBar
          value={searchInput}
          onChange={setSearchInput}
          onDebouncedChange={handleDebouncedSearch}
          placeholder={t("search_permission_groups")}
          className="sm:w-72"
        />

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <Icon icon={TbX} className="mr-1.5" />
            {t("clear_filters")}
          </Button>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {!isLoading && paginatedGroups.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4">
            <EmptyState
              variant="simple"
              title={
                hasActiveFilters
                  ? t("no_matches")
                  : t("no_permission_groups_found")
              }
              description={
                hasActiveFilters
                  ? t("try_different_search")
                  : t("create_your_first_permission_group")
              }
            />
            {hasActiveFilters ? (
              <Button variant="outline" onClick={clearFilters}>
                <Icon icon={TbX} className="mr-2" />
                {t("clear_filters")}
              </Button>
            ) : (
              canCreate && (
                <Button onClick={() => setFormDialog("create")}>
                  <Icon icon={TbPlus} className="mr-2" />
                  {t("add_permission_group")}
                </Button>
              )
            )}
          </div>
        ) : (
          <>
            <div className="min-h-0 flex-1 overflow-hidden">
              <div className="hidden h-full md:block">
                <PermissionGroupsTable
                  groups={paginatedGroups}
                  isLoading={isLoading && !data}
                  canUpdate={canUpdate}
                  canDelete={canDelete}
                  onEdit={setFormDialog}
                  onDelete={setDeleteTarget}
                />
              </div>
              <div className="h-full space-y-3 overflow-auto md:hidden">
                {isLoading && !data
                  ? Array.from({ length: 4 }).map((_, index) => (
                      <div
                        key={index}
                        className="h-36 animate-pulse rounded-xl border border-border bg-muted/40"
                      />
                    ))
                  : paginatedGroups.map((group) => (
                      <PermissionGroupCard
                        key={group.id}
                        group={group}
                        canUpdate={canUpdate}
                        canDelete={canDelete}
                        onEdit={setFormDialog}
                        onDelete={setDeleteTarget}
                      />
                    ))}
              </div>
            </div>

            {totalPages > 1 && (
              <div className="flex shrink-0 items-center justify-between gap-3 border-t border-border bg-muted/30 px-1 py-2 sm:px-2">
                <p className="text-xs text-muted-foreground">
                  {t("showing_results", {
                    start: (safePage - 1) * PERMISSION_GROUPS_PAGE_SIZE + 1,
                    end: Math.min(
                      safePage * PERMISSION_GROUPS_PAGE_SIZE,
                      total,
                    ),
                    total,
                  })}
                </p>
                <PaginationControls
                  currentPage={safePage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  isLoading={isFetching}
                />
              </div>
            )}
          </>
        )}
      </div>

      <PermissionGroupFormDialog
        open={formDialog !== null}
        group={formDialog === "create" ? null : formDialog}
        onClose={() => setFormDialog(null)}
        onSuccess={handleFormSuccess}
      />

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("delete_permission_group")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("delete_permission_group_confirmation", {
                name: deleteTarget?.name,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              {t("cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? t("deleting") : t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
